import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Menu, List, Icon, SemanticICONS, Popup } from 'semantic-ui-react';
import styled from 'styled-components';

import { iRootState, iRootDispatch } from '../store';
import { CurrentPageState, CurrentToggledMenuState } from '../store/types/temp-types';
import SubMenuItem from './SubMenuItem';
import { MenuItemProps, SubMenuItemProps } from './types';

type MenuItemWrapperProps = {
  activated: boolean;
  opened: boolean;
};

type AppMenuItemIconWrapperProps = {
  activated: boolean;
};

type MenuItemWrapperTextProps = {
  opened: boolean;
};

type AppMenuItemArrowIconProps = {
  activated: boolean;
  opened: boolean;
};

const MenuItemWrapper = styled(Menu.Item)<MenuItemWrapperProps & { isCollapsed?: boolean }>`
  position: relative;
  display: block;
  padding: ${props => {
    if (props.isCollapsed) return '20px 8px';
    return props.opened ? '16px 0 0' : '16px 0';
  }};
  font-size: 1rem;
  font-weight: 600;
  color: #aaadb1;
  cursor: pointer;
  background: ${props => (props.activated ? props.theme.main.color : '#fff')};
  transition: all 0.3s ease;
  min-height: ${props => props.isCollapsed ? '60px' : 'auto'};
  display: ${props => props.isCollapsed ? 'flex' : 'block'};
  align-items: center;
  justify-content: ${props => props.isCollapsed ? 'center' : 'flex-start'};

  &:hover {
    background: ${props => (props.activated ? props.theme.main.color : '#f6f7f7')};
  }

  > i,
  > p {
    color: ${props => (props.activated ? '#fff' : '#AAADB1')};
  }
`;

const AppMenuItemIconWrapper = styled.div<AppMenuItemIconWrapperProps & { isCollapsed?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: ${props => props.isCollapsed ? '0' : '0 16px'};
  width: ${props => props.isCollapsed ? 'auto' : 'auto'};
  min-width: ${props => props.isCollapsed ? '24px' : 'auto'};

  i {
    color: ${props => (props.activated ? '#fff' : '#AAADB1')};
    font-size: ${props => props.isCollapsed ? '1.2em' : '1em'} !important;
  }
`;

const MenuItemWrapperText = styled.p<MenuItemWrapperTextProps & { isCollapsed?: boolean }>`
  display: ${props => props.isCollapsed ? 'none' : 'inline-block'};
  margin-bottom: ${props => (props.opened ? '16px' : '0')};
  color: #aaadb1;
`;

const AppMenuItemArrowIcon = styled(Icon)<AppMenuItemArrowIconProps & { isCollapsed?: boolean }>`
  position: absolute;
  top: 16px;
  right: 12px;
  color: ${props => (props.activated || props.opened ? '#fff' : '#AAADB1')} !important;
  display: ${props => props.isCollapsed ? 'none' : 'block'};
`;

const SubMenuItemsList = styled(List)`
  display: block;
  width: 100%;
  padding: 0 !important;
  margin: 0 !important;
`;

const mapState = (state: iRootState) => ({
  currentToggledMenu: state.currentToggledMenu,
  currentPage: state.currentPage
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateCurrentToggledMenu: (state: CurrentToggledMenuState) =>
    dispatch.currentToggledMenu.updateCurrentToggledMenu(state),
  updateCurrentPage: (state: CurrentPageState) =>
    dispatch.currentPage.updateCurrentPage(state)
});

type Props = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch> & MenuItemProps & {
  isCollapsed?: boolean;
};

const MenuItem: React.FC<Props> = ({
  currentPage,
  currentToggledMenu,
  updateCurrentPage,
  updateCurrentToggledMenu,
  isCollapsed,
  ...item
}: Props) => {
  const [isSubMenuActivated, setIsSubMenuActivated] = useState(false);

  const history = useHistory();
  const match = useRouteMatch();

  const hasSubmenu = !!item?.submenuItems?.length;

  const handleChangeSelectedMenuBackground = (selectedItem: SubMenuItemProps) => {
    if (!hasSubmenu) return;

    // Quando a sidebar está colapsada, usa currentPage.parent.name
    // Quando está expandida, usa currentToggledMenu.name
    const shouldActivate = isCollapsed 
      ? selectedItem?.parentAlias === currentPage?.parent?.name
      : selectedItem?.parentAlias === currentToggledMenu?.name;
    
    setIsSubMenuActivated(shouldActivate);
  };

  // Atualiza o estado quando a sidebar é colapsada/expandida ou quando currentPage muda
  React.useEffect(() => {
    if (hasSubmenu && currentPage?.parent?.name === item.alias) {
      setIsSubMenuActivated(true);
    }
  }, [currentPage?.parent?.name, item.alias, hasSubmenu, isCollapsed]);

  const handlePageChange = (selectedItem: MenuItemProps) => {
    if (currentPage.name !== selectedItem.alias) {
      updateCurrentPage({
        id: selectedItem.id,
        name: selectedItem.alias,
        text: selectedItem.text,
        title: selectedItem.title
      });
      updateCurrentToggledMenu({});

      if (selectedItem.route) {
        history.push(`${match.url}/${selectedItem.route}`);
      }
    }
  };

  const handleCollapsedItemClick = (selectedItem: MenuItemProps) => {
    // Se tem submenu, redireciona para o primeiro item do submenu
    if (hasSubmenu && selectedItem.submenuItems && selectedItem.submenuItems.length > 0) {
      const firstSubmenuItem = selectedItem.submenuItems[0];
      updateCurrentPage({
        id: firstSubmenuItem.id,
        name: firstSubmenuItem.name,
        text: firstSubmenuItem.text,
        title: firstSubmenuItem.title,
        parent: {
          id: selectedItem.id,
          name: selectedItem.alias
        }
      });
      updateCurrentToggledMenu({});
      
      if (firstSubmenuItem.route) {
        history.push(`${match.url}/${firstSubmenuItem.route}`);
      }
    } else {
      // Se não tem submenu, funciona normalmente
      handlePageChange(selectedItem);
    }
  };

  const handleCurrentToggledMenuChange = (selectedItem: MenuItemProps) => {
    if (currentToggledMenu.name && currentToggledMenu.name === selectedItem.alias) {
      updateCurrentToggledMenu({});

      return;
    }

    updateCurrentToggledMenu({ id: selectedItem.id, name: selectedItem.alias });
  };

  const isOpened = Boolean(hasSubmenu && currentToggledMenu?.name === item.alias);

  let isActivated =
    currentPage?.name === item.alias || currentPage?.parent?.name === item.alias;

  // Para itens com submenu, usa isSubMenuActivated
  // Para itens sem submenu, mantém a lógica original
  isActivated = hasSubmenu ? isSubMenuActivated : isActivated;

  const menuItemContent = (
    <MenuItemWrapper
      as="a"
      activated={isActivated}
      opened={isOpened}
      isCollapsed={isCollapsed}
      onClick={() => 
        isCollapsed 
          ? handleCollapsedItemClick(item)
          : hasSubmenu 
            ? handleCurrentToggledMenuChange(item) 
            : handlePageChange(item)
      }
    >
      <AppMenuItemIconWrapper activated={isActivated} isCollapsed={isCollapsed}>
        <Icon name={item.icon as SemanticICONS} />
      </AppMenuItemIconWrapper>

      <MenuItemWrapperText opened={isOpened} isCollapsed={isCollapsed}>{item.text}</MenuItemWrapperText>

      {hasSubmenu && !isCollapsed && (
        <>
          <AppMenuItemArrowIcon
            className={`toggle-icon ${isOpened ? 'openedIcon' : 'closedIcon'}`}
            name={isOpened ? 'angle up' : 'angle down'}
            activated={isSubMenuActivated}
            isCollapsed={isCollapsed}
            position="right"
            size="large"
          />

          {isOpened && (
            <SubMenuItemsList>
              {item?.submenuItems?.map((submenuItem, index) => (
                <SubMenuItem
                  key={submenuItem.id}
                  first={index === 0}
                  {...submenuItem}
                  parent={item}
                  changeSelectedMenuBackground={handleChangeSelectedMenuBackground}
                />
              ))}
            </SubMenuItemsList>
          )}
        </>
      )}
    </MenuItemWrapper>
  );
  return menuItemContent;
};

export default connect(mapState, mapDispatch)(MenuItem);
